import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarPlusIcon, ChevronRightIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ButtonLink } from '../../components/ui/ButtonLink';
import { Button } from '../../components/ui/Button';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { EmptyState } from '../../components/ui/EmptyState';
import { Tag } from '../../components/ui/Tag';
import { FeatureSheet } from '../../components/organiser/FeatureSheet';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { budgetLabel } from '../../utils/event';
import { daysUntil, formatDate } from '../../utils/format';
import { rise, spring, stagger } from '../../utils/motion';
import type { MusicEvent } from '../../types/marketplace';
type Tab = 'upcoming' | 'drafts' | 'past';
export function Events() {
  const {
    events,
    getPerformer,
    hasMatch
  } = useMarketplace();
  const [tab, setTab] = useState<Tab>('upcoming');
  const [featuring, setFeaturing] = useState<MusicEvent | null>(null);
  const mine = events.filter(e => e.ownedByMe);
  const upcoming = mine.filter(e => e.status === 'live').sort((a, b) => a.date.localeCompare(b.date));
  const drafts = mine.filter(e => e.status === 'draft');
  const past = mine.filter(e => e.status === 'past');
  const hero = upcoming[0];
  const rest = tab === 'upcoming' ? upcoming.slice(1) : tab === 'drafts' ? drafts : past;
  const newApplicants = (e: MusicEvent) => e.applicantIds.filter(id => !hasMatch(e.id, id));
  return <div className="mx-auto w-full max-w-6xl px-5 pb-12 pt-6 lg:px-8 lg:pt-8" data-id="element-1012" data-mp="24nf3u391jy86">
      <div className="flex items-center justify-between gap-3" data-id="element-1013" data-mp="28rmtc2nbpi7g">
        <div data-id="element-1014" data-mp="1uqsmvpju5rbf">
          <h1 className="text-[28px] font-bold tracking-[-0.015em]" data-id="element-1015" data-mp="1usdg9buez47p">Events</h1>
          <p className="text-sm text-ink-3" data-id="element-1016" data-mp="1zqydr3ia79sg">Halcyon Rooftop · Somchai Rattanakul</p>
        </div>
        <ButtonLink to="/o/events/new" variant="accent" icon={<PlusIcon className="h-5 w-5" data-id="element-1018" data-mp="5bhvpghxkofz" />} data-id="element-1017" data-mp="5aqamzlym93y">
          New event
        </ButtonLink>
      </div>

      {hero ? <motion.section aria-labelledby="hero-event" initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={spring.heavy} className="mt-6 grid overflow-hidden rounded-lg bg-surface-2 lg:grid-cols-[1.15fr_1fr]" data-id="element-1019" data-mp="hneqktpmnkl">
          <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[400px]" data-id="element-1020" data-mp="o6yhg7umes1d">
            <img src={hero.photos[0]} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-1021" data-mp="k88wnu79687p" />
            <div className="absolute left-4 top-4 flex gap-2" data-id="element-1022" data-mp="f6jkucc7hqik">
              <Tag tone="overlay" data-id="element-1023" data-mp="aa2ynnokhm84">In {daysUntil(hero.date)} days</Tag>
              {hero.featured && <Tag tone="overlay" icon={<SparklesIcon className="h-3.5 w-3.5" data-id="element-1025" data-mp="2zzwr9iysnonc" />} data-id="element-1024" data-mp="2zzmy5migd1df">
                  Featured
                </Tag>}
            </div>
          </div>
          <div className="flex flex-col p-6 lg:p-8" data-id="element-1026" data-mp="2kw3ok4ug10gy">
            <p className="text-sm font-bold text-brand-strong" data-id="element-1027" data-mp="2kth9lk583iop">Next up</p>
            <h2 id="hero-event" className="mt-1 text-[32px] font-bold leading-[1.05] tracking-[-0.015em] lg:text-[40px]" data-id="element-1028" data-mp="2pvga8m9kw1hy">
              {hero.name}
            </h2>
            <p className="mt-2 text-[15px] text-ink-2" data-id="element-1029" data-mp="2zkzyk7g28nk7">
              {formatDate(hero.date, 'EEEE d MMMM')} · {hero.startTime} · {hero.venue}
            </p>

            <div className="mt-6" data-id="element-1030" data-mp="33o5yyyghrkir">
              <div className="flex items-baseline justify-between" data-id="element-1031" data-mp="38kmz6r0ytpc7">
                <p className="font-bold" data-id="element-1032" data-mp="38mzeewfc9llk">Line-up</p>
                <p className="tabular text-sm text-ink-2" data-id="element-1033" data-mp="3dlaqzex0qa7h">
                  {hero.slots.filter(s => s.filledBy).length} of {hero.slots.length} acts booked
                </p>
              </div>
              <div className="mt-2 flex gap-1.5" data-id="element-1034" data-mp="1j79siv5drp7x">
                {hero.slots.map(s => {
              const p = s.filledBy ? getPerformer(s.filledBy) : undefined;
              return <div key={s.id} className="flex-1" data-id="element-1035" data-mp="3eldl37iaegh9">
                      <div className={`h-2 rounded-full ${s.filledBy ? 'bg-brand' : s.pencilled ? 'bg-brand/40' : 'bg-line'}`} data-id="element-1036" data-mp="39nbywdpke11k" />
                      <p className="mt-1.5 truncate text-[13px] text-ink-3" data-id="element-1037" data-mp="3nnd2i0x7rmkq">{p ? p.name : s.kind}</p>
                    </div>;
            })}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3" data-id="element-1038" data-mp="2uxo0bk8rpghj">
              <div className="flex -space-x-3" data-id="element-1039" data-mp="2uw313hbid9dc">
                {newApplicants(hero).slice(0, 4).map(id => {
              const p = getPerformer(id);
              return p ? <img key={id} src={p.photos[0]} alt="" className="h-10 w-10 rounded-full border-2 border-surface-2 object-cover" data-id="element-1040" data-mp="2zxsgta9oeb7r" /> : null;
            })}
              </div>
              <p className="text-[15px]" data-id="element-1041" data-mp="2c5ldts9g35zd">
                <span className="tabular font-bold" data-id="element-1042" data-mp="279mtcfr8m848">{newApplicants(hero).length}</span> applicants waiting
              </p>
            </div>

            <div className="mt-auto flex flex-wrap gap-3 pt-8" data-id="element-1043" data-mp="mpj4pb6v83w4">
              <ButtonLink to={`/o/review/${hero.id}`} variant="accent" size="lg" data-id="element-1044" data-mp="rikljoqmudic">
                Review applicants
              </ButtonLink>
              <ButtonLink to={`/o/review/${hero.id}?tab=lineup`} variant="outline" size="lg" data-id="element-1045" data-mp="do1hl8v1j1xx">
                Line-up
              </ButtonLink>
              {!hero.featured && <Button variant="ghost" size="lg" icon={<SparklesIcon className="h-4 w-4" data-id="element-1047" data-mp="1ag50a2zonvp8" />} onClick={() => setFeaturing(hero)} data-id="element-1046" data-mp="16fl3x1mxow45">
                  Feature
                </Button>}
            </div>
          </div>
        </motion.section> : <EmptyState icon={<CalendarPlusIcon className="h-7 w-7" data-id="element-1049" data-mp="11ef98y9pqyrz" />} title="No live events" body="Post an event and performers will start applying." data-id="element-1048" data-mp="11exdoi6j4jaz">
          <ButtonLink to="/o/events/new" variant="accent" data-id="element-1050" data-mp="3h8xq8k3twa3o">
            Post your first event
          </ButtonLink>
        </EmptyState>}

      <div className="mt-10" data-id="element-1051" data-mp="3243165d0hd09">
        <SegmentedControl<Tab> ariaLabel="Event status" layoutId="events-tab" size="sm" value={tab} onChange={setTab} options={[{
        value: 'upcoming',
        label: 'Upcoming',
        count: Math.max(0, upcoming.length - 1)
      }, {
        value: 'drafts',
        label: 'Drafts',
        count: drafts.length
      }, {
        value: 'past',
        label: 'Past'
      }]} data-id="element-1052" data-mp="323bgvqov71pa" />
        <AnimatePresence mode="wait" data-id="element-1053" data-mp="2jb8ya9snxbce">
          <motion.ul key={tab} variants={stagger} initial="hidden" animate="show" exit={{
          opacity: 0,
          transition: {
            duration: 0.12
          }
        }} className="mt-4 divide-y divide-line" data-id="element-1054" data-mp="2edqtmvlohia7">
            {rest.length === 0 && <li className="py-8 text-center text-[15px] text-ink-3" data-id="element-1055" data-mp="25elx1utofz0o">
                {tab === 'upcoming' ? 'Nothing else coming up.' : tab === 'drafts' ? 'No drafts.' : 'No past events.'}
              </li>}
            {rest.map(e => <motion.li key={e.id} variants={rise} className="flex flex-wrap items-center gap-4 py-4 sm:flex-nowrap" data-id="element-1056" data-mp="b5q89uu3c46k">
                <img src={e.photos[0]} alt="" className="h-16 w-16 shrink-0 rounded-sm object-cover" data-id="element-1057" data-mp="g49vdykf7m0i" />
                <div className="min-w-0 flex-1" data-id="element-1058" data-mp="kv02bg8hvwxb">
                  <p className="truncate font-bold" data-id="element-1059" data-mp="kwbdw286m8yz">{e.name}</p>
                  <p className="truncate text-sm text-ink-2" data-id="element-1060" data-mp="ptl1dcc0gak6">
                    {formatDate(e.date)} · {budgetLabel(e)} · {e.slots.filter(s => s.filledBy).length}/{e.slots.length} booked
                  </p>
                  {tab === 'upcoming' && e.applicantIds.length === 0 && <p className="text-sm font-semibold text-brand-strong" data-id="element-1061" data-mp="24se049gfb16">No applicants yet — discover performers or feature it</p>}
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-2" data-id="element-1062" data-mp="1dk78n1e6xqt4">
                  {tab === 'upcoming' && <>
                      {!e.featured && <Button variant="ghost" size="sm" onClick={() => setFeaturing(e)} data-id="element-1063" data-mp="1hnn4hvifrn4r">
                          Feature
                        </Button>}
                      <Link to={`/o/review/${e.id}`} className="inline-flex h-9 items-center gap-1 rounded-full bg-surface-2 pl-4 pr-3 text-sm font-bold transition-colors duration-150 hover:bg-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-1064" data-mp="1yq2luzg17oco">
                        {newApplicants(e).length} to review <ChevronRightIcon className="h-4 w-4" data-id="element-1065" data-mp="2hdxuy60p8dzg" />
                      </Link>
                    </>}
                  {tab === 'drafts' && <ButtonLink to="/o/events/new" variant="secondary" size="sm" data-id="element-1066" data-mp="2rcnfy7nw6ejx">
                      Continue
                    </ButtonLink>}
                  {tab === 'past' && <Button variant="secondary" size="sm" onClick={() => toast.success('Review sent', {
                description: 'Thanks — reviews help great acts get booked.'
              })} data-id="element-1067" data-mp="wzxkhlhvkxaf">
                      Review performers
                    </Button>}
                </div>
              </motion.li>)}
          </motion.ul>
        </AnimatePresence>
      </div>
      <FeatureSheet event={featuring} onClose={() => setFeaturing(null)} data-id="element-1068" data-mp="33v9amh7mfc9g" />
    </div>;
}